import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar
} from "@mui/material";
import {
  Article,
  Description
} from "@mui/icons-material";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { generateCertificateOfAccreditation } from "./CertificateGenerator";
import { generateScopeOfAccreditation } from "./ScopeGenerator";
import { generateSCFAnnexure } from './SCFAnnexureGenerator';
import { decryptAppId, decryptText } from "../../utils/encryption";
import dayjs from "dayjs";


const ViewCertificate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scfLoading, setScfLoading] = useState(false);
  
  const [application, setApplication] = useState(null);
  const [certificateMainData, setCertificateMainData] = useState({});
  const [scopeData, setScopeData] = useState(null);
  const [scfData, setScfData] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [certificateNotAvailable, setCertificateNotAvailable] = useState(false);

  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedAction = searchParams.get("action");

  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgIdFromUrl = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const actionType = encryptedAction ? decryptText(encryptedAction) : null;

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch application data
  useEffect(() => {
    const fetchApplicationData = async () => {
      try {
        if (!appId || !orgIdFromUrl) {
          setError("Missing application or organization ID");
          setLoading(false);
          return;
        }

        // Check certificate availability first
        const visibilityResponse = await axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CertificateVisible?appid=${appId}&p=1`
        );
        
        if (visibilityResponse.data && visibilityResponse.data.isstatus === true) {
          setCertificateNotAvailable(true);
          setLoading(false);
          return;
        }

        // Fetch basic application details
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/ac-process/application/details/${appId}`);
        const data = response.data.data;
        setApplication(data);
        
        // Fetch scope and SCF data after application data is loaded
        await Promise.all([fetchScopeData(data), fetchSCFData()]);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching application data:", error);
        setError("Failed to fetch application data");
        setLoading(false);
      }
    };

    fetchApplicationData();
  }, [appId, orgIdFromUrl]);

  // Fetch scope data from API
  const fetchScopeData = async (appData = application) => {
    try {
      
      // Fetch certificate main data
      const certificatemainDataResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetMaincertificatedata?appid=${appId}&labtypeid=${appData?.LabTypeId || 3}`
      );
      
      // Fetch certificate data (test records)
      const certificateResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getcertificatedata?appid=${appId}&token=nablgvfdcrtyfbskgirwsoh`
      );
      
      // Fetch location data (certificate info)
      const locationResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/CertificateLocation?appid=${appId}&check=nablgvfdcrtyfbskgirwsoh`
      );
      
      const certificateData = certificateResponse.data?.certificate || [];
      const locationData = locationResponse.data?.certlocation?.[0] || {};
      const mainData = certificatemainDataResponse.data || {};
      
      // Transform API data to match ScopeGenerator format
      const transformedRecords = certificateData.map(record => ({
        discipline: record.disciplineName || '',
        productMaterial: record.productMaterial || '',
        specificTest: record.specifictest || '',
        testPerformedAt: record.testPerformedAt || '',
        scopeYear: record.scopeyear || '',
        unit: record.unit || '',
        measr: record.measr || '',
        range: record.range || '',
        heading1: record.heading1 || '',
        heading2: record.heading2 || '',
        heading3: record.heading3 || '',
        heading4: record.heading4 || '',
        heading5: record.heading5 || '',
        heading6: record.heading6 || '',
        groupName: record.groupName || '',
        testcode: record.testcode || ''
      }));
      
      // Prepare scope data
      const assessmentTypeId = appData?.AssessmentTypeId;
      const isNewCert = assessmentTypeId === 1 || assessmentTypeId === 2;

      const scopeDataObj = {
        labName: mainData.labName || appData?.LabName || appData?.Organization_Name || '',
        location: locationData.location || appData?.Address || '',
        address: mainData.address || '',
        standard: locationData.standard || '-',
        // certificateNumber: isNewCert ? (locationData.cert_No || '') : (mainData.oldCertificateNumber || locationData.cert_No || ''),
        certificateNumber: isNewCert ? (locationData.cert_No || '') : (mainData.cert_No || locationData.cert_No || ''),
        issueDate: locationData.issue_Date ? locationData.issue_Date : '',
        validUntil: locationData.validuntill ? locationData.validuntill : '',
        //amendmentDate: (appData?.AssessmentTypeId === 1 ) ? '' : locationData.amendmentDate ? dayjs(locationData.amendmentDate, ["MMM D YYYY h:mmA", "MMM DD YYYY h:mmA", "YYYY-MM-DD"]).format("DD/MM/YYYY") : '',
        amendmentDate: (appData?.AssessmentTypeId === 1 ) ? '' : locationData.amendmentDate ? locationData.amendmentDate : '',
        headerChange: locationData.isNew === '1',
        legalEntity: appData?.LabName || appData?.Organization_Name || '',
        additionaldata: mainData.additionaldata || '',
        applicationNo: mainData.applicationNo || '',
        field: appData?.LabTypeId === 1 ? 'TESTING' : 
               appData?.LabTypeId === 2 ? 'CALIBRATION' : 
               appData?.LabTypeId === 3 ? 'MEDICAL' : 
               appData?.LabTypeId === 4 ? 'PTP' : 
               appData?.LabTypeId === 5 ? 'RMP' : '',
        records: transformedRecords,
        // Additional fields from API
        labType: locationData.labType || appData?.LabType,
        labTypeId: locationData.LabTypeId || appData?.LabTypeId,
        validity: locationData.validity || '',
        permanent: locationData.permanent === '1',
        mobile: locationData.mobile === '1',
        site: locationData.site === '1',
        potc: locationData.potc === '1',
        appId: appId,
        orgId: orgIdFromUrl
      };
      
      // Update certificate main data with all fields
      const certificateMainDataObj = {
        ...mainData,
        labName: mainData.labName || appData?.LabName || appData?.Organization_Name || '',
        //amendmentDate: (appData?.AssessmentTypeId === 1 ) ? '' : locationData.amendmentDate ? dayjs(locationData.amendmentDate, ["MMM D YYYY h:mmA", "MMM DD YYYY h:mmA", "YYYY-MM-DD"]).format("DD/MM/YYYY") : '',
        amendmentDate: (appData?.AssessmentTypeId === 1 ) ? '' : locationData.amendmentDate ? locationData.amendmentDate : '',
        // certificateNumber: isNewCert ? (locationData.cert_No || '') : (mainData.oldCertificateNumber || locationData.cert_No || ''),
        certificateNumber: isNewCert ? (locationData.cert_No || '') : (mainData.cert_No || locationData.cert_No || ''),
        issuedate: mainData.issuedate || '',
        validtill: mainData.validtill || '',
        legalEntity: mainData?.legalEntity || '',
        field: mainData?.field || '',
        standard: locationData?.standard || '-',
        appId: appId,
        orgId: orgIdFromUrl
      };
      
      setCertificateMainData(certificateMainDataObj);
      setScopeData(scopeDataObj);
      return scopeDataObj;
      
    } catch (error) {
      console.error('Error fetching scope data:', error);
      return null;
    }
  };

  // Fetch SCF data from API
  const fetchSCFData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetSCFscope?appid=${appId}&token=nablgvfdcrtyfbskgirwsoh`
      );
      
      const data = response.data;
      
      if (data.isstatus && data.scope && data.scope.length > 0) {
        setScfData(data.scope);
        return data.scope;
      } else {
        setScfData([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching SCF data:', error);
      setScfData([]);
      return [];
    }
  };

  // Handle Certificate of Accreditation download
  const handleCertificateOfAccreditation = async () => {
    try {
      setCertificateLoading(true);

      // Check if certificate URL already exists
      if (actionType === 'download') {
        const checkResponse = await axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getletters?appid=${appId}&token=nablgvfdcrtyfbskgirwsoh`
        );

        if (checkResponse.data && checkResponse.data.maincertificateURL) {
          console.log('✅ Certificate URL found, opening existing file:', checkResponse.data.maincertificateURL);
          window.open(checkResponse.data.maincertificateURL, '_blank');
          setCertificateLoading(false);
          return;
        }
      }

      // URL doesn't exist, generate and upload
      console.log('⚙️ No existing certificate URL, generating new PDF...');

      const certificateData = {
        labName: certificateMainData?.labName || '',
        address: certificateMainData?.address || '',
        additionaldata: certificateMainData?.additionaldata || '',
        applicationNo: certificateMainData?.applicationNo || '',
        certificateNumber: certificateMainData?.certificateNumber || '',
        oldCertificateNumber: (application?.AssessmentTypeId === 1 || application?.AssessmentTypeId === 2 || application?.AssessmentTypeId === 22) ? (certificateMainData?.oldCertificateNumber || '') : '',
        issueDate: certificateMainData?.issuedate || '',
        validUntil: certificateMainData?.validtill || '',
        amendmentDate: certificateMainData?.amendmentDate || '',
        legalEntity: certificateMainData?.legalEntity || '',
        field: certificateMainData?.field || '',
        standard: certificateMainData?.standard || '',
        directorname: certificateMainData?.directorname || '',
        directorsignature: certificateMainData?.directorsignature || '',
        ceoname: certificateMainData?.ceoname || '',
        appId: appId,
        orgId: orgIdFromUrl,
        stage: application?.Stage || '',
        records: []
      };

      // Generate the PDF
      const { pdfBlob, fileName, doc } = await generateCertificateOfAccreditation(certificateData);

      // Upload to S3
      console.log('☁️  Uploading Certificate PDF to S3...');
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!uploadResponse.data.success) {
        throw new Error('Failed to upload file to S3');
      }

      const certificateUrl = uploadResponse.data.data.publicUrl;
      console.log(`✅ Certificate PDF uploaded to S3: ${certificateUrl}`);

      // Save certificate URL to database
      const updatedBy = localStorage.getItem('orgId') || '100';
      const uploadresponse = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Saveletters?appid=${appId}&URL=${certificateUrl}&updatedby=${updatedBy}&type=Main`,
      );

      console.log('✅ Certificate URL saved to database');

      if (uploadresponse.data.isstatus) {
        // Download the PDF
        doc.save(fileName);
        setCertificateLoading(false);
        showSnackbar('Certificate of Accreditation generated and saved successfully!', 'success');
      } else {
        setCertificateLoading(false);
        showSnackbar('Something went wrong while saving certificate URL', 'error');
      }
    } catch (error) {
      console.error('Error generating certificate:', error);
      setCertificateLoading(false);
      showSnackbar(`Error generating certificate: ${error.message || 'Please check if certificate images exist in public/certificate_images/ folder'}`, 'error');
    }
  };

  // Handle Scope of Accreditation download
  const handleScopeOfAccreditation = async () => {
    try {
      setScopeLoading(true);
      if(actionType === 'download'){
      const checkResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetscopeURL?appid=${appId}&token=nablgvfdcrtyfbskgirwsoh`
      );
      
      if (checkResponse.data && checkResponse.data.scopeURl) {
        console.log('✅ Scope URL found, opening existing file:', checkResponse.data.scopeURl);
        window.open(checkResponse.data.scopeURl, '_blank');
        setScopeLoading(false);
        return;
      }
    }
      
      // URL doesn't exist, generate and upload
      console.log('⚙️ No existing scope URL, generating new PDF...');
      
      // Generate the PDF
      const { pdfBlob, fileName, doc } = await generateScopeOfAccreditation(scopeData);
      
      // Upload to S3
      console.log('☁️  Uploading Scope PDF to S3...');
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!uploadResponse.data.success) {
        throw new Error('Failed to upload file to S3');
      }

      const scopeUrl =  uploadResponse.data.data.publicUrl;
      console.log(`✅ Scope PDF uploaded to S3: ${scopeUrl}`);
      
      // Save scope URL to database
      const updatedBy = localStorage.getItem('orgId') || '100';
      const uploadresponse = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/savescopeurl?appid=${appId}&scopeurl=${scopeUrl}&updatedby=${updatedBy}`);
      
      console.log('✅ Scope URL saved to database');
      
      if(uploadresponse.data.isstatus){
        // Download the PDF
        doc.save(fileName);
        setScopeLoading(false);
        showSnackbar('Scope of Accreditation generated and saved successfully!', 'success');
      } else {
        setScopeLoading(false);
        showSnackbar('Something went wrong', 'error');
      }
    } catch (error) {
      console.error('Error generating scope:', error);
      setScopeLoading(false);
      showSnackbar(`Error generating scope: ${error.message || 'Please try again'}`, 'error');
    }
  };

  // Handle SCF Annexure download
  const handleSCFAnnexure = async () => {
    try {
      setScfLoading(true);
      
      if(actionType === 'download'){
      const checkResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetscopeURL?appid=${appId}&token=nablgvfdcrtyfbskgirwsoh`
      );
      
      if (checkResponse.data && checkResponse.data.scFscopeURL) {
        console.log('✅ SCF Scope URL found, opening existing file:', checkResponse.data.scFscopeURL);
        window.open(checkResponse.data.scFscopeURL, '_blank');
        setScfLoading(false);
        return;
      }
    }
      
      // URL doesn't exist, generate and upload
      console.log('⚙️ No existing SCF scope URL, generating new PDF...');
      
      const certificateData = {
        labName: certificateMainData?.labName || '',
        address: certificateMainData?.address || '',
        additionaldata: certificateMainData?.additionaldata || '',
        applicationNo: certificateMainData?.applicationNo || '',
        certificateNumber: certificateMainData?.certificateNumber || '',
        issueDate: certificateMainData?.issuedate ? certificateMainData?.issuedate : '',
        validUntil: certificateMainData?.validtill ? certificateMainData?.validtill : '',
        amendmentDate: certificateMainData?.amendmentDate || '',
        legalEntity: certificateMainData?.legalEntity || '',
        field: certificateMainData?.field || '',
        standard: certificateMainData?.standard || '',
        directorname: certificateMainData?.directorname || '',
        directorsignature: certificateMainData?.directorsignature || '',
        ceoname: certificateMainData?.ceoname || '',
        appId: appId,
        orgId: orgIdFromUrl,
        records: []
      };
      
      // Generate the PDF
      const result = await generateSCFAnnexure(certificateData, scfData);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      const { pdfBlob, fileName, doc } = result;
      
      // Upload to S3
      console.log('☁️  Uploading SCF Annexure PDF to S3...');
      const formData = new FormData();
      formData.append('file', pdfBlob, fileName);

      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (!uploadResponse.data.success) {
        throw new Error('Failed to upload file to S3');
      }

      const scopeUrl = uploadResponse.data.data.publicUrl;
      console.log(`✅ SCF Annexure PDF uploaded to S3: ${scopeUrl}`);
      
      // Save SCF scope URL to database
      const updatedBy = localStorage.getItem('orgId') || '100';
      const uploadresponse = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/savescfscopeurl?appid=${appId}&scopeurl=${scopeUrl}&updatedby=${updatedBy}`
      );
      
      console.log('✅ SCF Scope URL saved to database');
      
      if (uploadresponse.data.isstatus) {
        // Download the PDF
        doc.save(fileName);
        setScfLoading(false);
        showSnackbar('SCF Annexure generated and saved successfully!', 'success');
      } else {
        setScfLoading(false);
        showSnackbar('Something went wrong while saving SCF URL', 'error');
      }
    } catch (error) {
      console.error('Error generating SCF Annexure:', error);
      setScfLoading(false);
      showSnackbar(`Error generating SCF Annexure: ${error.message || 'Please try again'}`, 'error');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (certificateNotAvailable) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f5f7fa",
          p: 3
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 6,
            borderRadius: 3,
            textAlign: "center",
            maxWidth: 500
          }}
        >
          <Alert severity="warning" sx={{ mb: 3 }}>
            Certificate Not Available
          </Alert>
          <Typography variant="h6" gutterBottom>
            Certificate Not Available Right Now, Please Try After Sometime.
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  if (application && application.Stage === '7d' &&  certificateMainData && (certificateMainData.certificateNumber === "" || !certificateMainData.validtill )) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f5f7fa",
          p: 3
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 6,
            borderRadius: 3,
            textAlign: "center",
            maxWidth: 500
          }}
        >
          <Alert severity="warning" sx={{ mb: 3 }}>
            Certificate Not Available
          </Alert>
          <Typography variant="h6" gutterBottom>
            Certificate Not Available Right Now, Please contact the administrator.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f5f7fa"
      }}
    >
      <Paper
        elevation={4}
        sx={{
          width: 420,
          p: 4,
          borderRadius: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {actionType === 'download' ? 'Download Certificate' : 'Regenerate Certificate'}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {actionType === 'download' ? 'Select the document you want to download:' : ''}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Certificate of Accreditation Button */}
          <Button
            variant="outlined"
            size="large"
            startIcon={
              certificateLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Article />
              )
            }
            onClick={handleCertificateOfAccreditation}
            disabled={certificateLoading || scopeLoading || scfLoading}
            sx={{
              py: 2,
              justifyContent: "flex-start",
              textTransform: "none",
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                borderColor: "#1565c0",
                backgroundColor: "rgba(25, 118, 210, 0.04)"
              }
            }}
          >
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Certificate of Accreditation
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {certificateLoading
                  ? "Generating certificate..."
                  : "Download the official certificate"}
              </Typography>
            </Box>
          </Button>

          {/* Scope of Accreditation Button */}
          <Button
            variant="outlined"
            size="large"
            startIcon={
              scopeLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Description />
              )
            }
            onClick={handleScopeOfAccreditation}
            disabled={scopeLoading || certificateLoading || scfLoading || !scopeData?.records || scopeData.records.length === 0}
            sx={{
              py: 2,
              justifyContent: "flex-start",
              textTransform: "none",
              borderColor: "#2e7d32",
              color: "#2e7d32",
              "&:hover": {
                borderColor: "#1b5e20",
                backgroundColor: "rgba(46, 125, 50, 0.04)"
              }
            }}
          >
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Scope of Accreditation
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {scopeLoading
                  ? "Generating scope..."
                  : !scopeData?.records || scopeData.records.length === 0
                  ? "No scope data available"
                  : `Download the scope details (${scopeData.records.length} records)`}
              </Typography>
            </Box>
          </Button>

          {/* SCF Annexure Button */}
          <Button
            variant="outlined"
            size="large"
            startIcon={
              scfLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Description />
              )
            }
            onClick={handleSCFAnnexure}
            disabled={scfLoading || certificateLoading || scopeLoading || !scfData || scfData.length === 0}
            sx={{
              py: 2,
              justifyContent: "flex-start",
              textTransform: "none",
              borderColor: "#ed6c02",
              color: "#ed6c02",
              "&:hover": {
                borderColor: "#e65100",
                backgroundColor: "rgba(237, 108, 2, 0.04)"
              }
            }}
          >
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                SCF Annexure
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {scfLoading
                  ? "Generating SCF Annexure..."
                  : !scfData || scfData.length === 0
                  ? "No SCF data available"
                  : `Download the SCF Annexure (${scfData.length} records)`}
              </Typography>
            </Box>
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViewCertificate;